import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  Typography,
  Input,
  Select,
  Pagination,
  Spin,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  storefrontApi,
  getProductConfig,
  type ProductListItem,
  type CategoryItem,
  type ProductConfig,
} from '@/apis/storefrontApi';
import styles from './index.module.less';

const { Title, Paragraph } = Typography;

/** 列表主图只展示第一张，兼容接口返回 string 或 string[] */
function getFirstImage(image: ProductListItem['image']): string | null {
  if (image == null) return null;
  return Array.isArray(image) ? (image[0] ?? null) : image;
}

const Products: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get('category') || '';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const [search, setSearch] = useState('');
  const category = categoryFromUrl || 'all';
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState<ProductListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const pageSize = 8;
  const [productConfig, setProductConfig] = useState<ProductConfig | undefined>(
    { description: '' }
  );

  const setListParams = (next: { category?: string; page?: number }) => {
    const cat = next.category !== undefined ? next.category : category;
    const pg = next.page !== undefined ? next.page : page;
    const params: Record<string, string> = {};
    if (cat && cat !== 'all') params.category = cat;
    if (pg > 1) params.page = String(pg);
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    storefrontApi
      .getCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
    getProductConfig()
      .then(setProductConfig)
      .catch(() => setProductConfig({ description: '' }));
  }, [i18n.language]);

  useEffect(() => {
    setLoading(true);

    storefrontApi
      .getProducts({
        page,
        pageSize,
        category: category === 'all' ? undefined : category,
        keyword: search || undefined,
      })
      .then(res => {
        setList(res.list);
        setTotal(res.total);
      })
      .catch(() => {
        setList([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [page, pageSize, category, search, i18n.language]);

  const categoryOptions = [
    { value: 'all', label: t('products.allCategories') },
    ...categories.map(c => ({ value: c.key, label: c.title })),
  ];
  if (category !== 'all' && !categoryOptions.some(o => o.value === category)) {
    categoryOptions.push({ value: category, label: category });
  }

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <Title level={2} className={styles.title}>
          {t('products.title')}
        </Title>
        <Paragraph className={styles.desc}>
          {productConfig?.description}
        </Paragraph>

        <div className={styles.toolbar}>
          <Input
            placeholder={t('products.searchPlaceholder')}
            prefix={<SearchOutlined />}
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setListParams({ page: 1 });
            }}
            className={styles.search}
            allowClear
          />
          <Select
            value={category}
            onChange={v => {
              setListParams({ category: v, page: 1 });
            }}
            className={styles.select}
            options={categoryOptions}
          />
        </div>

        {loading ? (
          <div className={styles.loading}>
            <Spin size='large' />
          </div>
        ) : (
          <>
            <Row gutter={[24, 24]}>
              {list.map(p => {
                const coverSrc = getFirstImage(p.image);
                return (
                  <Col xs={24} sm={12} md={8} lg={6} key={p.id}>
                    <Link
                      to={`/products/${p.id}`}
                      state={{ listSearch: searchParams.toString() }}
                      className={styles.cardLink}
                    >
                      <Card
                        className={styles.card}
                        hoverable
                        cover={
                          <div className={styles.cover}>
                            {coverSrc ? (
                              <img
                                src={coverSrc}
                                alt={p.name}
                                className={styles.coverImg}
                              />
                            ) : (
                              <span className={styles.coverPlaceholder}>
                                {t('products.placeholder')}
                              </span>
                            )}
                          </div>
                        }
                      >
                        <Title level={5} className={styles.cardTitle}>
                          {p.name}
                        </Title>
                        <Paragraph
                          type='secondary'
                          className={styles.cardSummary}
                          ellipsis={{ rows: 2 }}
                        >
                          {p.summary}
                        </Paragraph>
                        <div className={styles.cardPrice}>${p.price}</div>
                      </Card>
                    </Link>
                  </Col>
                );
              })}
            </Row>
            {total > pageSize && (
              <div className={styles.pagination}>
                <Pagination
                  current={page}
                  pageSize={pageSize}
                  total={total}
                  onChange={p => setListParams({ page: p })}
                  showSizeChanger={false}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Products;
